import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertOctagon, Banknote, Building2, FileWarning, HandCoins, Landmark, Receipt, Scale, ShoppingCart, Truck, Wallet,
} from "lucide-react";
import api from "../../lib/api";
import { fmtMoney, INVOICE_STATUS } from "../../lib/labels";
import { Chip, KpiCard, PageHeader } from "../../components/common";
import { DemoBanner, Money } from "../../components/finance";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function FinDashboard() {
  const { data } = useQuery({
    queryKey: ["fin-dashboard"],
    queryFn: async () => (await api.get("/finanzas/dashboard")).data,
  });
  if (!data) return null;
  const { proveedores, compras, facturas, excepciones, remesas, clientes, presupuesto, pagos_proximos_7d } = data;

  return (
    <div data-testid="fin-dashboard">
      <PageHeader title="Finanzas y Compras" subtitle="Cuadro de mando económico: compras, facturas, pagos, cobros y presupuesto." />
      <DemoBanner />

      <h2 className="ts-section-title mb-3 mt-6">Atención inmediata</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testid="kpi-excepciones" title="Excepciones abiertas" value={excepciones.abiertas} subtitle={`${excepciones.criticas} críticas`} icon={AlertOctagon} tone={excepciones.criticas ? "danger" : "warning"} to="/finanzas/excepciones" />
        <KpiCard testid="kpi-facturas-validacion" title="Facturas por validar" value={facturas.pendientes_validacion} subtitle={`${facturas.con_excepcion} con excepción · ${facturas.con_advertencias} con advertencias`} icon={FileWarning} tone="warning" to="/finanzas/facturas" />
        <KpiCard testid="kpi-remesas" title="Remesas en curso" value={remesas.en_curso} subtitle={fmtMoney(remesas.importe_en_curso)} icon={Banknote} tone="info" to="/finanzas/remesas" />
        <KpiCard testid="kpi-cobros" title="Por cobrar" value={fmtMoney(clientes.por_cobrar)} subtitle={`${clientes.vencidas} vencidas`} icon={HandCoins} tone={clientes.vencidas ? "danger" : "success"} to="/finanzas/facturacion" />
      </div>

      <h2 className="ts-section-title mb-3 mt-8">Ciclo de compra</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testid="kpi-solicitudes" title="Solicitudes activas" value={compras.solicitudes_activas} subtitle={`${compras.solicitudes_pendientes_aprobacion} pendientes de aprobación`} icon={ShoppingCart} tone="info" to="/finanzas/solicitudes" />
        <KpiCard testid="kpi-ofertas" title="Ofertas por validar" value={compras.ofertas_pendientes} icon={Scale} tone="muted" to="/finanzas/ofertas" />
        <KpiCard testid="kpi-ordenes" title="Órdenes abiertas" value={compras.ordenes_abiertas} icon={Truck} tone="info" to="/finanzas/ordenes" />
        <KpiCard testid="kpi-conformidades" title="Conformidades pendientes" value={compras.conformidades_pendientes} icon={Receipt} tone="warning" to="/finanzas/recepciones" />
      </div>

      <h2 className="ts-section-title mb-3 mt-8">Proveedores y presupuesto</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testid="kpi-proveedores" title="Proveedores activos" value={`${proveedores.activos}/${proveedores.total}`} subtitle={`${proveedores.pendientes} por validar · ${proveedores.bloqueados} bloqueados`} icon={Building2} tone={proveedores.cambios_bancarios ? "warning" : "muted"} to="/finanzas/proveedores" />
        <KpiCard testid="kpi-banco" title="Cambios bancarios" value={proveedores.cambios_bancarios} subtitle="Pendientes de verificación fuera de banda" icon={Landmark} tone={proveedores.cambios_bancarios ? "danger" : "success"} to="/finanzas/proveedores" />
        <KpiCard testid="kpi-presupuesto" title="Presupuesto comprometido" value={fmtMoney(presupuesto.comprometido)} subtitle={`Disponible ${fmtMoney(presupuesto.disponible)} de ${fmtMoney(presupuesto.total)}`} icon={Wallet} tone="info" to="/finanzas/presupuesto" />
        <KpiCard testid="kpi-forecast" title="Partidas sobre forecast" value={presupuesto.sobre_forecast} subtitle={`Facturado ${fmtMoney(presupuesto.facturado)} · Pagado ${fmtMoney(presupuesto.pagado)}`} icon={Wallet} tone={presupuesto.sobre_forecast ? "warning" : "muted"} to="/finanzas/presupuesto" />
      </div>

      <h2 className="ts-section-title mb-3 mt-8">Pagos próximos (7 días)</h2>
      <div className="ts-card overflow-hidden" data-testid="proximos-pagos">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Factura</TableHead><TableHead>Proveedor</TableHead><TableHead>Vencimiento</TableHead>
              <TableHead>Estado</TableHead><TableHead className="text-right">Importe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagos_proximos_7d.length === 0 && <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-slate-500">Sin pagos aprobados con vencimiento en 7 días.</TableCell></TableRow>}
            {pagos_proximos_7d.map((inv) => (
              <TableRow key={inv.id} data-testid={`proximo-pago-${inv.id}`}>
                <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                <TableCell>{inv.supplier_name}</TableCell>
                <TableCell className="text-xs">{(inv.due_date || "").slice(0, 10)}</TableCell>
                <TableCell><Chip map={INVOICE_STATUS} value={inv.status} /></TableCell>
                <TableCell className="text-right"><Money value={inv.total} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
